import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { FiMinus, FiInfo, FiClock, FiTarget, FiChevronDown, FiChevronUp } from 'react-icons/fi'
import { theme } from '../theme'
import { analyticsAPI } from '../services/api'
import { LoadingSpinner } from './ui'

const TimelineContainer = styled.div`
  margin-top: ${theme.spacing.lg};
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.primary.light}30;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.secondary.beige};
`

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.md};
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
`

const Title = styled.h3`
  margin: 0 0 ${theme.spacing.sm} 0;
  color: ${theme.colors.secondary.black};
  font-size: 18px;
  font-weight: 600;
`

const ChartContainer = styled.div`
  position: relative;
  height: 280px;
  margin: ${theme.spacing.md} 0;
  border: 1px solid ${theme.colors.primary.light}30;
  border-radius: ${theme.borderRadius.sm};
  background: white;
  overflow: hidden;
`

const ChartSvg = styled.svg`
  width: 100%;
  height: 100%;
`

const ChartPoint = styled.circle`
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    r: ${props => props.r + 2};
    stroke: ${theme.colors.primary.main};
    stroke-width: 2;
  }
`

const ChartLine = styled.path`
  fill: none;
  stroke: ${theme.colors.primary.main};
  stroke-width: 2;
  opacity: 0.6;
`

const ChartAxis = styled.line`
  stroke: ${theme.colors.primary.light};
  stroke-width: 1;
`

const ChartLabel = styled.text`
  font-size: 11px;
  fill: ${theme.colors.primary.dark};
  text-anchor: middle;
`

const Tooltip = styled.div`
  position: fixed;
  background: ${theme.colors.secondary.black};
  color: white;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 1000;
  transform: translate(-50%, -100%);
  margin-top: -8px;
  min-width: 200px;
  max-width: 300px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    border: 4px solid transparent;
    border-top-color: ${theme.colors.secondary.black};
    transform: translateX(-50%);
  }
`

const WindowDetails = styled.div`
  margin-top: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.primary.light}50;
  border-radius: ${theme.borderRadius.sm};
  background: white;
`

const WindowGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.sm};
`

const WindowStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const StatLabel = styled.div`
  font-size: 11px;
  color: ${theme.colors.primary.light};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const StatValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${theme.colors.secondary.black};
`

const ErrorMessage = styled.div`
  padding: ${theme.spacing.md};
  background: ${theme.colors.status.error + '20'};
  color: ${theme.colors.status.error};
  border-radius: ${theme.borderRadius.sm};
  border: 1px solid ${theme.colors.status.error + '30'};
  text-align: center;
`

const EmptyState = styled.div`
  padding: ${theme.spacing.xl};
  text-align: center;
  color: ${theme.colors.primary.dark};
  
  svg {
    font-size: 48px;
    margin-bottom: ${theme.spacing.md};
  }
`

const TableContainer = styled.div`
  margin-top: ${theme.spacing.lg};
  border: 1px solid ${theme.colors.primary.light}30;
  border-radius: ${theme.borderRadius.md};
  background: white;
  overflow: hidden;
`

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.md};
  background: ${theme.colors.primary.main}10;
  border-bottom: 1px solid ${theme.colors.primary.light}30;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: ${theme.colors.primary.main}20;
  }
`

const TableTitle = styled.h4`
  margin: 0;
  color: ${theme.colors.secondary.black};
  font-size: 16px;
  font-weight: 600;
`

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.primary.main};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.sm};
  transition: background-color 0.2s ease;
  
  &:hover {
    background: ${theme.colors.primary.main}20;
  }
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`

const TableHead = styled.thead`
  background: ${theme.colors.primary.light}20;
`

const TableHeaderCell = styled.th`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  text-align: left;
  font-weight: 600;
  color: ${theme.colors.secondary.black};
  border-bottom: 1px solid ${theme.colors.primary.light}30;
  white-space: nowrap;
`

const TableBody = styled.tbody``

const TableRow = styled.tr`
  border-bottom: 1px solid ${theme.colors.primary.light}20;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: ${theme.colors.primary.light}10;
  }
  
  &:last-child {
    border-bottom: none;
  }
`

const TableCell = styled.td`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  color: ${theme.colors.secondary.black};
  vertical-align: top;
`

const ScoreCell = styled(TableCell)`
  font-weight: 600;
  color: ${props => props.score === null ? theme.colors.primary.light : theme.colors.secondary.black};
`

const ConfidenceBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  background: ${props => {
    const colors = {
      'insufficient': theme.colors.primary.light + '40',
      'very-low': '#ff6b6b40',
      'low': '#ffa72640',
      'moderate': '#ffeb3b40',
      'high': '#66bb6a40',
      'very-high': '#43a04740'
    }
    return colors[props.level] || colors['insufficient']
  }};
  color: ${props => {
    const colors = {
      'insufficient': theme.colors.primary.light,
      'very-low': '#d32f2f',
      'low': '#f57c00',
      'moderate': '#f9a825',
      'high': '#388e3c',
      'very-high': '#2e7d32'
    }
    return colors[props.level] || colors['insufficient']
  }};
`

const DateRange = styled.div`
  font-size: 13px;
  color: ${theme.colors.primary.dark};
`

const SpanDays = styled.div`
  font-size: 12px;
  color: ${theme.colors.primary.light};
  margin-top: 2px;
`

const MobileChartContainer = styled.div`
  margin-top: ${theme.spacing.lg};
  border: 1px solid ${theme.colors.primary.light}30;
  border-radius: ${theme.borderRadius.md};
  background: white;
  overflow: hidden;
`

const MobileChartHeader = styled.div`
  padding: ${theme.spacing.md};
  background: ${theme.colors.primary.main}10;
  border-bottom: 1px solid ${theme.colors.primary.light}30;
`

const MobileChartTitle = styled.h4`
  margin: 0;
  color: ${theme.colors.secondary.black};
  font-size: 16px;
  font-weight: 600;
`

const MobileChartWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 9/16;
  max-height: 80vh;
  background: white;
  overflow: hidden;
`

const MobileChartSvg = styled.svg`
  width: 100%;
  height: 100%;
`

const MobileChartPoint = styled.circle`
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    r: ${props => props.r + 2};
    stroke: ${theme.colors.primary.main};
    stroke-width: 2;
  }
`

const MobileChartLine = styled.path`
  fill: none;
  stroke: ${theme.colors.primary.main};
  stroke-width: 2;
  opacity: 0.6;
`

const MobileChartAxis = styled.line`
  stroke: ${theme.colors.primary.light};
  stroke-width: 1;
`

const MobileChartLabel = styled.text`
  font-size: 12px;
  fill: ${theme.colors.primary.dark};
  text-anchor: middle;
`

const MobileChartGridLine = styled.line`
  stroke: ${theme.colors.primary.light}30;
  stroke-width: 0.5;
  stroke-dasharray: 2,2;
`

const ReferenceLine = styled.line`
  stroke: ${theme.colors.primary.main};
  stroke-width: 2;
  stroke-dasharray: 4,4;
  opacity: 0.8;
`

const MobileReferenceLine = styled.line`
  stroke: ${theme.colors.primary.main};
  stroke-width: 2;
  stroke-dasharray: 4,4;
  opacity: 0.8;
`

const SolidReferenceLine = styled.line`
  stroke: ${theme.colors.primary.main};
  stroke-width: 2;
  opacity: 0.8;
`

const MobileSolidReferenceLine = styled.line`
  stroke: ${theme.colors.primary.main};
  stroke-width: 2;
  opacity: 0.8;
`

const ResilienceTimeline = ({ userId }) => {
  const [timelineData, setTimelineData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedWindow, setSelectedWindow] = useState(null)
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, content: '' })
  const [showTable, setShowTable] = useState(false)

  useEffect(() => {
    if (userId) {
      loadTimelineData()
    }
  }, [userId])

  const loadTimelineData = async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      const response = await analyticsAPI.getResilienceTimeline(userId)
      setTimelineData(response.data.data)
    } catch (error) {
      console.error('Failed to load timeline:', error)
      setError(error.response?.data?.error || 'Failed to load resilience timeline')
    } finally {
      setIsLoading(false)
    }
  }

  const getConfidenceColor = (level) => {
    const colors = {
      'insufficient': '#cccccc',
      'very-low': '#ff6b6b',
      'low': '#ffa726',
      'moderate': '#ffeb3b',
      'high': '#66bb6a',
      'very-high': '#43a047'
    }
    return colors[level] || colors['insufficient']
  }

  const getConfidenceRadius = (level) => {
    const radii = {
      'insufficient': 3,
      'very-low': 4,
      'low': 5,
      'moderate': 6,
      'high': 7,
      'very-high': 8
    }
    return radii[level] || radii['insufficient']
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const renderDataTable = () => {
    if (!timelineData?.windows || timelineData.windows.length === 0) return null

    return (
      <TableContainer>
        <TableHeader onClick={() => setShowTable(!showTable)}>
          <TableTitle>Resilience Timeline Data Points</TableTitle>
          <ToggleButton>
            {showTable ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />}
          </ToggleButton>
        </TableHeader>
        
        {showTable && (
          <Table>
            <TableHead>
              <tr>
                <TableHeaderCell>Window</TableHeaderCell>
                <TableHeaderCell>Resilience Score</TableHeaderCell>
                <TableHeaderCell>Confidence</TableHeaderCell>
                <TableHeaderCell>Date Range</TableHeaderCell>
                <TableHeaderCell>Entries</TableHeaderCell>
                <TableHeaderCell>Data Quality</TableHeaderCell>
              </tr>
            </TableHead>
            <TableBody>
              {timelineData.windows.map((window, index) => (
                <TableRow 
                  key={window.windowId}
                  onClick={() => setSelectedWindow(window)}
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell>{window.windowId}</TableCell>
                  <ScoreCell score={window.resilience.score}>
                    {window.resilience.score !== null 
                      ? Math.round(window.resilience.score)
                      : 'Unable to calculate'
                    }
                  </ScoreCell>
                  <TableCell>
                    <ConfidenceBadge level={window.resilience.confidence?.level || 'insufficient'}>
                      {window.resilience.confidence?.level || 'insufficient'}
                    </ConfidenceBadge>
                  </TableCell>
                  <TableCell>
                    <DateRange>
                      {formatDate(window.dateRange.startDate)} - {formatDate(window.dateRange.endDate)}
                    </DateRange>
                    <SpanDays>{window.dateRange.spanDays} days</SpanDays>
                  </TableCell>
                  <TableCell>{window.entriesRange.count} entries</TableCell>
                  <TableCell>{window.dataQuality?.pressureRange?.variety || 'Unknown'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    )
  }

  const renderMobileChart = () => {
    if (!timelineData?.windows || timelineData.windows.length === 0) return null

    // Filter out windows with invalid scores for plotting
    const validWindows = timelineData.windows.filter(w => w.resilience.score !== null)
    
    if (validWindows.length === 0) return null

    // Get start and end dates from the full dataset
    const allWindows = timelineData.windows
    const startDate = allWindows[0]?.dateRange?.startDate
    const endDate = allWindows[allWindows.length - 1]?.dateRange?.endDate

    return (
      <MobileChartContainer>
        <MobileChartHeader>
          <MobileChartTitle>Mobile View - Resilience Timeline</MobileChartTitle>
        </MobileChartHeader>
        
        <MobileChartWrapper>
          <MobileChartSvg viewBox="0 0 400 700" preserveAspectRatio="xMidYMid meet">
            {/* Define padding for the chart area */}
            {(() => {
              const padding = { top: 40, right: 60, bottom: 80, left: 50 }
              const chartWidth = 400 - padding.left - padding.right
              const chartHeight = 700 - padding.top - padding.bottom

              // Y-axis always shows full 0-100 range for consistent reference
              const yMin = 0
              const yMax = 100

              const xScale = (index) => padding.left + (index / (validWindows.length - 1)) * chartWidth
              const yScale = (score) => {
                return padding.top + chartHeight - ((score - yMin) / (yMax - yMin)) * chartHeight
              }

              // Generate trend line points for valid windows
              const trendPoints = validWindows.map((window, index) => {
                return `${xScale(index)},${yScale(window.resilience.score)}`
              }).join(' ')

              return (
                <>
                  {/* Y-axis */}
                  <MobileChartAxis
                    x1={padding.left}
                    y1={padding.top}
                    x2={padding.left}
                    y2={padding.top + chartHeight}
                  />
                  
                  {/* X-axis */}
                  <MobileChartAxis
                    x1={padding.left}
                    y1={padding.top + chartHeight}
                    x2={400 - padding.right}
                    y2={padding.top + chartHeight}
                  />

                  {/* Y-axis grid lines and labels every 10 points */}
                  {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(score => (
                    <g key={score}>
                      {/* Grid line */}
                      <MobileChartGridLine
                        x1={padding.left}
                        y1={yScale(score)}
                        x2={400 - padding.right}
                        y2={yScale(score)}
                      />
                      {/* Y-axis tick */}
                      <MobileChartAxis
                        x1={padding.left - 5}
                        y1={yScale(score)}
                        x2={padding.left}
                        y2={yScale(score)}
                      />
                      {/* Y-axis label with background */}
                      <rect
                        x={padding.left - 35}
                        y={yScale(score) - 8}
                        width="30"
                        height="16"
                        fill="white"
                        rx="3"
                      />
                      <MobileChartLabel x={padding.left - 20} y={yScale(score) + 4} textAnchor="middle">
                        {score}
                      </MobileChartLabel>
                    </g>
                  ))}

                  {/* Trend line - only for valid scores */}
                  {validWindows.length > 1 && <MobileChartLine d={`M ${trendPoints}`} />}

                  {/* Reference line at Y=85 */}
                  <MobileReferenceLine
                    x1={padding.left}
                    y1={yScale(85)}
                    x2={400 - padding.right}
                    y2={yScale(85)}
                  />

                  {/* Solid reference line at Y=65 */}
                  <MobileSolidReferenceLine
                    x1={padding.left}
                    y1={yScale(65)}
                    x2={400 - padding.right}
                    y2={yScale(65)}
                  />

                  {/* Data points - only valid windows */}
                  {validWindows.map((window, index) => {
                    const yPos = yScale(window.resilience.score)
                    
                    return (
                      <MobileChartPoint
                        key={window.windowId}
                        cx={xScale(index)}
                        cy={yPos}
                        r={6}
                        fill={theme.colors.primary.light}
                        stroke="white"
                        strokeWidth="1"
                        onMouseEnter={(e) => handlePointHover(e, window)}
                        onMouseLeave={handlePointLeave}
                        onClick={() => setSelectedWindow(window)}
                      />
                    )
                  })}

                  {/* X-axis labels - only start and end dates */}
                  {(() => {
                    if (validWindows.length === 0) return null
                    
                    const startLabel = startDate ? formatDate(startDate) : 'Start'
                    const endLabel = endDate ? formatDate(endDate) : 'End'
                    
                    return (
                      <>
                        {/* Start date label */}
                        <g>
                          <rect
                            x={padding.left - 30}
                            y={padding.top + chartHeight + 10}
                            width="60"
                            height="20"
                            fill="white"
                            rx="4"
                          />
                          <MobileChartLabel
                            x={padding.left}
                            y={padding.top + chartHeight + 22}
                            fontWeight="bold"
                            fill={theme.colors.secondary.black}
                            textAnchor="middle"
                          >
                            {startLabel}
                          </MobileChartLabel>
                        </g>
                        
                        {/* End date label */}
                        <g>
                          <rect
                            x={400 - padding.right - 30}
                            y={padding.top + chartHeight + 10}
                            width="60"
                            height="20"
                            fill="white"
                            rx="4"
                          />
                          <MobileChartLabel
                            x={400 - padding.right}
                            y={padding.top + chartHeight + 22}
                            fontWeight="bold"
                            fill={theme.colors.secondary.black}
                            textAnchor="middle"
                          >
                            {endLabel}
                          </MobileChartLabel>
                        </g>
                      </>
                    )
                  })()}
                </>
              )
            })()}
          </MobileChartSvg>

          {/* Tooltip */}
          {tooltip.show && (
            <Tooltip
              style={{
                left: tooltip.x,
                top: tooltip.y
              }}
            >
              {tooltip.content.split('\n').map((line, index) => (
                <div key={index}>{line}</div>
              ))}
            </Tooltip>
          )}
        </MobileChartWrapper>
      </MobileChartContainer>
    )
  }

  const handlePointHover = (event, window) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const chartContainer = event.currentTarget.closest('div')
    const containerRect = chartContainer.getBoundingClientRect()
    
    let content
    if (window.resilience.score === null) {
      content = `Score: Unable to calculate
${formatDate(window.dateRange.startDate)} - ${formatDate(window.dateRange.endDate)}
Constant pressure (no variation to analyze)
${window.entriesRange.count} entries over ${window.dateRange.spanDays} days`
    } else {
      content = `Score: ${Math.round(window.resilience.score)}
${formatDate(window.dateRange.startDate)} - ${formatDate(window.dateRange.endDate)}
${window.resilience.confidence?.level} confidence
${window.entriesRange.count} entries over ${window.dateRange.spanDays} days`
    }

    // Calculate tooltip position, keeping it within the chart container
    let tooltipX = rect.left + rect.width / 2
    let tooltipY = rect.top - 10
    
    // Adjust horizontal position to keep tooltip in view
    const tooltipWidth = 250 // estimated tooltip width
    if (tooltipX + tooltipWidth / 2 > containerRect.right) {
      tooltipX = containerRect.right - tooltipWidth / 2 - 10
    }
    if (tooltipX - tooltipWidth / 2 < containerRect.left) {
      tooltipX = containerRect.left + tooltipWidth / 2 + 10
    }
    
    // Adjust vertical position to keep tooltip in view
    if (tooltipY < containerRect.top + 60) {
      tooltipY = rect.bottom + 20 // Show below the point instead
    }

    setTooltip({
      show: true,
      x: tooltipX,
      y: tooltipY,
      content
    })
  }

  const handlePointLeave = () => {
    setTooltip({ show: false, x: 0, y: 0, content: '' })
  }

  const renderChart = () => {
    if (!timelineData?.windows || timelineData.windows.length === 0) return null

    // Filter out windows with invalid scores for plotting
    const validWindows = timelineData.windows.filter(w => w.resilience.score !== null)
    
    if (validWindows.length === 0) return null

    const chartWidth = 800
    const chartHeight = 260
          const padding = { top: 20, right: 40, bottom: 50, left: 35 }
    const plotWidth = chartWidth - padding.left - padding.right
    const plotHeight = chartHeight - padding.top - padding.bottom

    // Y-axis always shows full 0-100 range for consistent reference
    const yMin = 0
    const yMax = 100

    const xScale = (index) => padding.left + (index / (validWindows.length - 1)) * plotWidth
    const yScale = (score) => {
      return padding.top + plotHeight - ((score - yMin) / (yMax - yMin)) * plotHeight
    }

    // Generate trend line points for valid windows
    const trendPoints = validWindows.map((window, index) => {
      return `${xScale(index)},${yScale(window.resilience.score)}`
    }).join(' ')

    return (
      <ChartContainer>
        <ChartSvg viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
          {/* Y-axis */}
          <ChartAxis
            x1={padding.left}
            y1={padding.top}
            x2={padding.left}
            y2={chartHeight - padding.bottom}
          />
          
          {/* X-axis */}
          <ChartAxis
            x1={padding.left}
            y1={chartHeight - padding.bottom}
            x2={chartWidth - padding.right}
            y2={chartHeight - padding.bottom}
          />

          {/* Y-axis grid lines and labels */}
          {[0, 20, 40, 60, 80, 100].map(score => (
            <g key={score}>
              {/* Grid line */}
              <line
                x1={padding.left}
                y1={yScale(score)}
                x2={chartWidth - padding.right}
                y2={yScale(score)}
                stroke={score === 0 || score === 100 ? theme.colors.primary.light : theme.colors.primary.light + '30'}
                strokeWidth={score === 0 || score === 100 ? '1' : '0.5'}
                strokeDasharray={score === 0 || score === 100 ? 'none' : '2,2'}
              />
              {/* Y-axis tick */}
              <ChartAxis
                x1={padding.left - 5}
                y1={yScale(score)}
                x2={padding.left}
                y2={yScale(score)}
              />
              {/* Y-axis label with background */}
              <rect
                x={padding.left - 26}
                y={yScale(score) - 10}
                width="24"
                height="18"
                fill="white"
                rx="3"
              />
              <ChartLabel x={padding.left - 14} y={yScale(score) + 3} textAnchor="middle">
                {score}
              </ChartLabel>
            </g>
          ))}

          {/* Trend line - only for valid scores */}
          {validWindows.length > 1 && <ChartLine d={`M ${trendPoints}`} />}

          {/* Reference line at Y=85 */}
          <ReferenceLine
            x1={padding.left}
            y1={yScale(85)}
            x2={chartWidth - padding.right}
            y2={yScale(85)}
          />

          {/* Solid reference line at Y=65 */}
          <SolidReferenceLine
            x1={padding.left}
            y1={yScale(65)}
            x2={chartWidth - padding.right}
            y2={yScale(65)}
          />

          {/* Data points - only valid windows */}
          {validWindows.map((window, index) => {
            const yPos = yScale(window.resilience.score)
            
            return (
              <ChartPoint
                key={window.windowId}
                cx={xScale(index)}
                cy={yPos}
                r={getConfidenceRadius(window.resilience.confidence?.level)}
                fill={getConfidenceColor(window.resilience.confidence?.level)}
                stroke="white"
                strokeWidth="1"
                onMouseEnter={(e) => handlePointHover(e, window)}
                onMouseLeave={handlePointLeave}
                onClick={() => setSelectedWindow(window)}
              />
            )
          })}

          {/* X-axis date labels (show first, last, and few intermediate points) */}
          {(() => {
            const labelIndices = []
            const totalValidWindows = validWindows.length
            
            if (totalValidWindows > 0) {
              // Always show first window
              labelIndices.push(0)
              
              // Show intermediate points (3-4 points between first and last)
              if (totalValidWindows > 2) {
                const intermediateCount = Math.min(4, totalValidWindows - 2)
                for (let i = 1; i <= intermediateCount; i++) {
                  const index = Math.floor((i * (totalValidWindows - 1)) / (intermediateCount + 1))
                  if (index > 0 && index < totalValidWindows - 1) {
                    labelIndices.push(index)
                  }
                }
              }
              
              // Always show last window (most recent)
              if (totalValidWindows > 1) {
                labelIndices.push(totalValidWindows - 1)
              }
            }
            
            // Remove duplicates and sort
            const uniqueIndices = [...new Set(labelIndices)].sort((a, b) => a - b)
            
                         return uniqueIndices.map(index => {
               const window = validWindows[index]
               const isLastWindow = index === totalValidWindows - 1
               const labelText = formatDate(window.dateRange.endDate) + (isLastWindow ? ' (latest)' : '')
               const labelWidth = labelText.length * 6 + 16 // Approximate width with more padding
               
               return (
                 <g key={window.windowId}>
                   {/* X-axis label background */}
                   <rect
                     x={xScale(index) - labelWidth/2}
                     y={chartHeight - padding.bottom + 6}
                     width={labelWidth}
                     height="20"
                     fill="white"
                     rx="4"
                   />
                   <ChartLabel
                     x={xScale(index)}
                     y={chartHeight - padding.bottom + 18}
                     fontWeight={isLastWindow ? 'bold' : 'normal'}
                     fill={isLastWindow ? theme.colors.secondary.black : theme.colors.primary.dark}
                     textAnchor="middle"
                   >
                     {labelText}
                   </ChartLabel>
                 </g>
               )
             })
          })()}
        </ChartSvg>

        {/* Tooltip */}
        {tooltip.show && (
          <Tooltip
            style={{
              left: tooltip.x,
              top: tooltip.y
            }}
          >
            {tooltip.content.split('\n').map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </Tooltip>
        )}
      </ChartContainer>
    )
  }

  if (isLoading) {
    return (
      <TimelineContainer>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: theme.spacing.xl }}>
          <LoadingSpinner />
          <span style={{ marginLeft: theme.spacing.sm }}>Loading timeline...</span>
        </div>
      </TimelineContainer>
    )
  }

  if (error) {
    return (
      <TimelineContainer>
        <ErrorMessage>
          <FiInfo style={{ marginRight: theme.spacing.sm }} />
          {error}
        </ErrorMessage>
      </TimelineContainer>
    )
  }

  if (!timelineData || timelineData.totalWindows === 0) {
    return (
      <TimelineContainer>
        <EmptyState>
          <FiClock />
          <div>No timeline data available</div>
          <div style={{ fontSize: '14px', marginTop: theme.spacing.sm }}>
            At least 8 journal entries are needed for rolling window analysis.
          </div>
        </EmptyState>
      </TimelineContainer>
    )
  }

  return (
    <TimelineContainer>
      <TimelineHeader>
        <div>
                    <Title>Resilience Timeline</Title>
        </div>
        <div style={{ fontSize: '12px', color: theme.colors.primary.light }}>
          {(() => {
            const invalidCount = timelineData.windows ? timelineData.windows.filter(w => w.resilience.score === null).length : 0
            const validCount = timelineData.totalWindows - invalidCount
            
            let message = `${timelineData.totalWindows} windows from ${timelineData.totalEntries} entries`
            if (invalidCount > 0) {
              message += ` (${invalidCount} window${invalidCount === 1 ? '' : 's'} could not be calculated)`
            }
            
            return message
          })()}
        </div>
      </TimelineHeader>

      {renderChart()}

      {renderDataTable()}

      {renderMobileChart()}

      {selectedWindow && (
        <WindowDetails>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing.sm }}>
            <h4 style={{ margin: 0, fontSize: '16px' }}>
              Window {selectedWindow.windowId} Details
            </h4>
            <button
              onClick={() => setSelectedWindow(null)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                color: theme.colors.text.secondary
              }}
            >
              ×
            </button>
          </div>
          
          <WindowGrid>
            <WindowStat>
              <StatLabel>Resilience Score</StatLabel>
              <StatValue>
                {selectedWindow.resilience.score !== null 
                  ? Math.round(selectedWindow.resilience.score)
                  : 'Unable to calculate'}
              </StatValue>
            </WindowStat>
            
            <WindowStat>
              <StatLabel>Confidence Level</StatLabel>
              <StatValue style={{ color: getConfidenceColor(selectedWindow.resilience.confidence?.level) }}>
                {selectedWindow.resilience.confidence?.level}
              </StatValue>
            </WindowStat>
            
            <WindowStat>
              <StatLabel>Date Range</StatLabel>
              <StatValue>
                {formatDate(selectedWindow.dateRange.startDate)} - {formatDate(selectedWindow.dateRange.endDate)}
              </StatValue>
            </WindowStat>
            
            <WindowStat>
              <StatLabel>Entries</StatLabel>
              <StatValue>{selectedWindow.entriesRange.count} entries</StatValue>
            </WindowStat>
            
            <WindowStat>
              <StatLabel>Time Span</StatLabel>
              <StatValue>{selectedWindow.dateRange.spanDays} days</StatValue>
            </WindowStat>
            
            <WindowStat>
              <StatLabel>Data Quality</StatLabel>
              <StatValue>{selectedWindow.dataQuality?.pressureRange?.variety || 'Unknown'}</StatValue>
            </WindowStat>
          </WindowGrid>

          {selectedWindow.resilience.interpretation && (
            <div style={{ 
              marginTop: theme.spacing.md, 
              padding: theme.spacing.sm,
              background: theme.colors.background.surface,
              borderRadius: theme.borderRadius.sm,
              fontSize: '14px',
              fontStyle: 'italic'
            }}>
              {selectedWindow.resilience.interpretation}
            </div>
          )}
        </WindowDetails>
      )}
    </TimelineContainer>
  )
}

export default ResilienceTimeline 